import React, { useEffect, useState } from "react";
import { api, errMsg, openSignedDoc } from "@/lib/api";
import { StatusPill, EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import ActionDialog from "@/components/admin/ActionDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Flag, ShieldAlert, HeartHandshake, Inbox, CheckCircle2, BadgeCheck, FileText, Archive } from "lucide-react";
import { toast } from "sonner";

export default function Complaints() {
  const [complaints, setComplaints] = useState(null);
  const [archived, setArchived] = useState(null);
  const [flags, setFlags] = useState(null);
  const [nonprofits, setNonprofits] = useState(null);

  const load = () => {
    api.get("/admin/complaints", { params: { status: "active" } }).then((r) => setComplaints(r.data)).catch(() => setComplaints([]));
    api.get("/admin/complaints", { params: { status: "resolved" } }).then((r) => setArchived(r.data)).catch(() => setArchived([]));
    api.get("/admin/risk-flags", { params: { status: "open" } }).then((r) => setFlags(r.data)).catch(() => setFlags([]));
    api.get("/admin/nonprofit-queue").then((r) => setNonprofits(r.data)).catch(() => setNonprofits([]));
  };
  useEffect(() => { load(); }, []);

  const resolveComplaint = async (id, notes) => { await api.post(`/admin/complaints/${id}/resolve`, { notes }); toast.success("Resolved."); load(); };
  const flagAction = async (id, kind, notes) => { await api.post(`/admin/risk-flags/${id}/${kind}`, { notes }); toast.success(`Flag ${kind}d.`); load(); };
  const npAction = async (id, action, notes) => { await api.post(`/admin/nonprofits/${id}/${action}`, { notes }); toast.success(`Nonprofit ${action}.`); load(); };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Complaints &amp; Risk</h1>
        <p className="mt-1 text-sm text-ff-medium">Trust &amp; Safety workflows: complaints, risk flags, and nonprofit verification.</p>
      </div>

      <Tabs defaultValue="complaints">
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList className="bg-[#F3F4F6]">
            <TabsTrigger value="complaints" data-testid="tab-complaints">Complaints ({complaints?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="flags" data-testid="tab-flags">Risk Flags ({flags?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="nonprofits" data-testid="tab-nonprofits">Nonprofit Queue ({nonprofits?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="archive" data-testid="tab-archive">Archive ({archived?.length ?? 0})</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="complaints" className="pt-5">
          {complaints === null ? <InlineLoader /> : complaints.length === 0 ? (
            <EmptyState icon={Inbox} title="No active complaints" message="Resolved complaints move to the Archive tab." testid="complaints-empty" />
          ) : (
            <div className="space-y-3" data-testid="complaints-list">
              {complaints.map((c) => (
                <div key={c.id} className="flex items-start justify-between gap-3 rounded-xl border border-ff-border bg-white p-4 shadow-sm">
                  <div>
                    <div className="flex items-center gap-2"><Flag className="h-4 w-4 text-ff-red" /><span className="font-heading font-bold text-ff-blue">{c.reason}</span><StatusPill status={c.status} /></div>
                    <div className="mt-1 text-sm text-ff-dark">{c.description}</div>
                    <div className="mt-1 text-xs text-ff-medium">Campaign: {c.campaign_title}</div>
                  </div>
                  {["open", "under_review"].includes(c.status) && (
                    <ActionDialog testid={`resolve-complaint-${c.id}`} trigger={<Button size="sm" className="bg-ff-green text-white hover:bg-ff-green/90"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Resolve</Button>}
                      title="Resolve complaint" requireNotes confirmLabel="Resolve" onConfirm={(n) => resolveComplaint(c.id, n)} />
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="flags" className="pt-5">
          {flags === null ? <InlineLoader /> : flags.length === 0 ? (
            <EmptyState icon={ShieldAlert} title="No open risk flags" message="All clear." testid="flags-empty" />
          ) : (
            <div className="space-y-3" data-testid="flags-list">
              {flags.map((f) => (
                <div key={f.id} className="flex items-start justify-between gap-3 rounded-xl border border-ff-border bg-white p-4 shadow-sm">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${f.severity === "high" ? "bg-[#FEF2F2] text-ff-red" : "bg-[#FFF7ED] text-ff-amber"}`}>{f.severity}</span>
                      <span className="font-heading font-bold text-ff-blue">{f.type}</span>
                    </div>
                    <div className="mt-1 text-sm text-ff-dark">{f.description}</div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <ActionDialog testid={`resolve-flag-${f.id}`} trigger={<Button size="sm" variant="outline" className="border-ff-green text-ff-green">Resolve</Button>}
                      title="Resolve risk flag" confirmLabel="Resolve" onConfirm={(n) => flagAction(f.id, "resolve", n)} />
                    <ActionDialog testid={`dismiss-flag-${f.id}`} trigger={<Button size="sm" variant="ghost">Dismiss</Button>}
                      title="Dismiss risk flag" confirmLabel="Dismiss" onConfirm={(n) => flagAction(f.id, "dismiss", n)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="nonprofits" className="pt-5">
          {nonprofits === null ? <InlineLoader /> : nonprofits.length === 0 ? (
            <EmptyState icon={HeartHandshake} title="No pending verifications" message="No nonprofit verifications awaiting review." testid="nonprofits-empty" />
          ) : (
            <div className="space-y-3" data-testid="nonprofits-list">
              {nonprofits.map((v) => (
                <div key={v.id} className="flex items-start justify-between gap-3 rounded-xl border border-ff-border bg-white p-4 shadow-sm">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><HeartHandshake className="h-4 w-4 text-ff-green" /><span className="font-heading font-bold text-ff-blue">{v.org_name}</span><StatusPill status={v.status} /></div>
                    <div className="mt-1 text-sm text-ff-dark">EIN: {v.ein}</div>
                    <div className="text-xs text-ff-medium">{v.org_address} · {v.contact_email}</div>
                    {(v.organizer_name || v.organizer_email) && (
                      <div className="text-xs text-ff-medium">Organizer: {v.organizer_name || "—"}{v.organizer_email ? ` · ${v.organizer_email}` : ""}</div>
                    )}
                    {v.documents?.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid={`np-docs-${v.id}`}>
                        {v.documents.map((doc, i) => (
                          <button key={i} type="button" onClick={() => openSignedDoc(doc).catch((e) => toast.error(errMsg(e)))}
                            className="inline-flex items-center gap-1 rounded-full border border-ff-border bg-[#F3F4F6] px-2.5 py-1 text-xs font-semibold text-ff-blue hover:border-ff-blue">
                            <FileText className="h-3 w-3" /> {doc.name || `Document ${i + 1}`}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-1 text-xs italic text-ff-medium" data-testid={`np-nodocs-${v.id}`}>No documents attached.</div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <ActionDialog testid={`np-approve-${v.id}`} trigger={<Button size="sm" className="bg-ff-green text-white hover:bg-ff-green/90"><BadgeCheck className="mr-1 h-3.5 w-3.5" /> Verify</Button>}
                      title="Approve nonprofit" confirmLabel="Verify" onConfirm={(n) => npAction(v.id, "approve", n)} />
                    <ActionDialog testid={`np-reject-${v.id}`} trigger={<Button size="sm" variant="ghost" className="text-ff-red">Reject</Button>}
                      title="Reject nonprofit" requireNotes destructive confirmLabel="Reject" onConfirm={(n) => npAction(v.id, "reject", n)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="archive" className="pt-5">
          {archived === null ? <InlineLoader /> : archived.length === 0 ? (
            <EmptyState icon={Archive} title="Archive is empty" message="Resolved complaints are automatically archived here." testid="archive-empty" />
          ) : (
            <div className="space-y-3" data-testid="archive-list">
              {archived.map((c) => (
                <div key={c.id} className="flex items-start justify-between gap-3 rounded-xl border border-ff-border bg-[#FAFAFA] p-4 shadow-sm" data-testid={`archived-complaint-${c.id}`}>
                  <div>
                    <div className="flex items-center gap-2"><Archive className="h-4 w-4 text-ff-medium" /><span className="font-heading font-bold text-ff-blue">{c.reason}</span><StatusPill status={c.status} /></div>
                    <div className="mt-1 text-sm text-ff-dark">{c.description}</div>
                    <div className="mt-1 text-xs text-ff-medium">Campaign: {c.campaign_title}</div>
                    {c.resolution_notes && <div className="mt-1 text-xs text-ff-medium"><span className="font-semibold text-ff-dark">Resolution:</span> {c.resolution_notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}